import React, { useMemo, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { DashboardHeaderWidget } from './types';
import { useWebMapContext, type CrossFilter } from '../webMap/WebMapContext';

type CategoryWidget = Extract<DashboardHeaderWidget, { type: 'header_category' }>;

const HeaderCategoryFilterControl: React.FC<{ widget: CategoryWidget }> = ({ widget }) => {
  const ctx = useWebMapContext();

  const field = widget.schemaField;
  const layerId = widget.layerId;
  const multi = widget.selectionMode === 'multiple';
  const filterKey = `header_cat_${widget.id}`;

  const allValues = useMemo(() => {
    if (!ctx || !field) return [];
    const valueSet = new Set<string>();
    const sourceLayers = layerId
      ? ctx.layers.filter((layer) => layer.id === layerId)
      : ctx.layers;
    for (const lyr of sourceLayers) {
      const features = ctx.layerFeatures.get(lyr.id) ?? [];
      for (const f of features) {
        const v = f.properties?.[field];
        if (v != null) valueSet.add(String(v));
      }
    }
    return Array.from(valueSet).sort();
  }, [ctx, field, layerId]);

  useEffect(() => {
    const existing = ctx?.crossFilters.get(filterKey);
    if (
      existing
      && (existing.field !== field || (existing.layerId ?? undefined) !== (layerId ?? undefined))
    ) {
      ctx?.removeCrossFilter(filterKey);
    }
  }, [ctx, filterKey, field, layerId]);

  const selectedValues = useMemo(() => {
    const cf = ctx?.crossFilters.get(filterKey);
    if (!cf) return [] as string[];
    const v = cf.value;
    return Array.isArray(v) ? v.map(String) : [String(v)];
  }, [ctx, filterKey]);

  const handleChange = useCallback(
    (values: string[]) => {
      if (!ctx) return;
      if (values.length === 0) {
        ctx.removeCrossFilter(filterKey);
      } else {
        const isExclude = widget.categoryOperator === 'exclude';
        if (isExclude) {
          const included = allValues.filter((v) => !values.includes(v));
          if (included.length === 0 || included.length === allValues.length) {
            ctx.removeCrossFilter(filterKey);
          } else {
            const cf: CrossFilter = { field, value: included, source: 'header', layerId };
            ctx.setCrossFilter(filterKey, cf);
          }
        } else {
          const cf: CrossFilter = { field, value: multi ? values : values[0], source: 'header', layerId };
          ctx.setCrossFilter(filterKey, cf);
        }
      }
    },
    [ctx, filterKey, field, layerId, multi, widget.categoryOperator, allValues]
  );

  if (!field) {
    return (
      <Typography variant="body2" color="text.secondary">
        Configure the schema field in widget settings.
      </Typography>
    );
  }

  if (allValues.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No values for "{field}"{layerId ? ' on the selected layer' : ''}
      </Typography>
    );
  }

  const label = widget.label || field;

  if (widget.presentation === 'inline') {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{label}:</Typography>
        {allValues.map((v) => {
            const active = selectedValues.includes(v);
            return (
              <Chip
                key={v}
                label={v}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                color={active ? 'primary' : 'default'}
                onClick={() => {
                  if (multi) {
                    const next = active
                      ? selectedValues.filter((s) => s !== v)
                      : [...selectedValues, v];
                    handleChange(next);
                  } else {
                    handleChange(active ? [] : [v]);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              />
            );
          })}
      </Stack>
    );
  }

  if (multi) {
    return (
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={allValues}
        value={selectedValues}
        onChange={(_, next) => handleChange(next)}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder={widget.placeholderText || 'Filter…'} />
        )}
        renderTags={(value) =>
          value.length > 0 ? (
            <Chip label={`${value.length} Selected`} size="small" color="primary" />
          ) : null
        }
        sx={{ minWidth: 180 }}
      />
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedValues[0] ?? ''}
        label={label}
        onChange={(e) => {
          const v = e.target.value;
          handleChange(v ? [v] : []);
        }}
      >
        <MenuItem value="">
          <em>All</em>
        </MenuItem>
        {allValues.map((v) => (
          <MenuItem key={v} value={v}>{v}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default HeaderCategoryFilterControl;
